import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import ReadMoreIcon from '@mui/icons-material/ReadMore';
import RemoveCircleOutline from '@mui/icons-material/RemoveCircleOutline';
import type { GridRenderCellParams, GridRowModel } from '@mui/x-data-grid-pro';
import { GridActionsCell, GridActionsCellItem } from '@mui/x-data-grid-pro';

import useAuth from 'portal/src/auth/useAuth';

import type { ActionColumnProps } from './addActionColumnProps';

const seeDetailsLabel = 'See Details';
const deactivateLabel = 'Deactivate';
const reactivateLabel = 'Reactivate';
const deleteLabel = 'Delete';

const ActionColumn = <TRow extends GridRowModel = GridRowModel>({
  extraActions,
  readActionConfig,
  changeActiveDisabledForRow,
  onChangeActiveStatus,
  deleteDisabledForRow,
  onDeleteClick,
  params,
}: ActionColumnProps<TRow> & {
  params: GridRenderCellParams<TRow>;
}): JSX.Element => {
  const { hasReadOnlyAccess } = useAuth();
  const isReadOnly = hasReadOnlyAccess();

  const { id, row } = params as { id: string; row: TRow };
  const { isActive } = row as { isActive?: boolean };

  const extraActionBtns = extraActions?.(row) ?? [];
  const hasMoreMenuActions = !!(
    extraActionBtns.length &&
    (onChangeActiveStatus || onDeleteClick)
  );

  const shouldHideReadAction =
    readActionConfig?.readHiddenForRow?.(row) ?? false;
  const shouldDisableChangeActive = changeActiveDisabledForRow?.(row) ?? false;
  const shouldDisableDelete = deleteDisabledForRow?.(row) ?? false;

  return (
    <GridActionsCell {...params}>
      {readActionConfig?.onClick && !shouldHideReadAction && (
        <GridActionsCellItem
          icon={<ReadMoreIcon sx={{ fontSize: 25 }} />}
          label={seeDetailsLabel}
          title={seeDetailsLabel}
          onClick={() => readActionConfig.onClick?.(id, row)}
          showInMenu={false}
        />
      )}
      {extraActionBtns.map(
        ({ disabled, icon, label, onClick, downloadHref }, index) => {
          return (
            <GridActionsCellItem
              key={index}
              disabled={disabled}
              {...(downloadHref && {
                component: 'a',
                href: downloadHref,
              })}
              icon={icon}
              {...(!hasMoreMenuActions && { title: label })}
              label={label}
              onClick={onClick}
              showInMenu={hasMoreMenuActions ? true : undefined}
            />
          );
        },
      )}
      {onChangeActiveStatus && typeof isActive === 'boolean' && (
        <GridActionsCellItem
          disabled={isReadOnly || shouldDisableChangeActive}
          icon={isActive ? <RemoveCircleOutline /> : <AddCircleOutlineIcon />}
          label={isActive ? deactivateLabel : reactivateLabel}
          {...(!hasMoreMenuActions && {
            title: isActive ? deactivateLabel : reactivateLabel,
          })}
          onClick={() => onChangeActiveStatus(id, isActive)}
          showInMenu={hasMoreMenuActions}
        />
      )}
      {onDeleteClick && (
        <GridActionsCellItem
          disabled={isReadOnly || shouldDisableDelete}
          icon={<DeleteIcon />}
          label={deleteLabel}
          {...(!hasMoreMenuActions && { title: deleteLabel })}
          onClick={() => onDeleteClick(id)}
          showInMenu={hasMoreMenuActions}
        />
      )}
    </GridActionsCell>
  );
};

export default ActionColumn;
